import { destinationShape } from './track-login-destination';

/**
 * Why `AuthGuard` sent a visitor on a private page to `/login`.
 *
 * Added for the login bounce of 30/09/2026: 14 of 658 interview sessions in a week hopped to
 * `/login` a moment after the worker sent a message, while the backend kept answering 200 for
 * the same user and the token was still in storage. The guard was the only code able to do it,
 * and it did it silently, so the one question that mattered (what state was it looking at?) had
 * no answer. These lines are that answer.
 *
 * Emitted on the O2 logs stream like `login_destination`, and for the same reason: PostHog
 * loses events around a navigation, which is exactly the moment these fire.
 *
 *   -- how often, and why. `recovery_failed` means storage held a valid token and `/me` still
 *   -- could not vouch for it twice in a row: the network, not the credential.
 *   SELECT reason, count(*) FROM "<logs-stream>" WHERE event = 'auth_guard_redirect' GROUP BY reason
 *
 *   -- the divergence itself: state had lost a session that storage still held.
 *   SELECT outcome, count(*) FROM "<logs-stream>" WHERE event = 'auth_session_recovery' GROUP BY outcome
 */
export type AuthGuardRedirectReason =
  /** Nothing in storage: a visitor who is not signed in. The ordinary case. */
  | 'no_token'
  /** Something in storage, but expired or unreadable. */
  | 'invalid_token'
  /** A valid token in storage, the session was re-checked once, and it still could not be restored. */
  | 'recovery_failed';

interface AuthGuardRedirectEvent {
  reason: AuthGuardRedirectReason;
  isReady: boolean;
  isAuthenticated: boolean;
  tokenPresent: boolean;
  tokenValid: boolean;
  path: string;
}

interface SessionRecoveryEvent {
  outcome: 'started' | 'recovered' | 'failed';
  path: string;
}

/** Fire-and-forget: no-op on the server, inert until the SDK is initialised, never throws. */
const log = (level: 'info' | 'warn', event: string, fields: Record<string, unknown>) => {
  if (typeof window === 'undefined') {
    return;
  }

  // Lazily imported for the same reason as `trackLoginDestination`: the browser-only SDK chains
  // to an ESM-only dependency that crashes SSR if it reaches the server bundle.
  import('@openobserve/browser-logs')
    .then(({ openobserveLogs }) => {
      openobserveLogs.logger[level](event, { event, ...fields });
    })
    .catch(() => {
      // Swallow: an uninitialised SDK must never surface to the user.
    });
};

export function trackAuthGuardRedirect({
  reason,
  isReady,
  isAuthenticated,
  tokenPresent,
  tokenValid,
  path,
}: AuthGuardRedirectEvent): void {
  log(reason === 'no_token' ? 'info' : 'warn', 'auth_guard_redirect', {
    reason,
    is_ready: isReady,
    is_authenticated: isAuthenticated,
    token_present: tokenPresent,
    token_valid: tokenValid,
    path: destinationShape(path),
  });
}

export function trackSessionRecovery({ outcome, path }: SessionRecoveryEvent): void {
  log(outcome === 'failed' ? 'warn' : 'info', 'auth_session_recovery', {
    outcome,
    path: destinationShape(path),
  });
}
